import React, { useState } from 'react';

/**
 * ValidityEditor komponent för att redigera giltighetstid på offert
 * 
 * @param {Object} offert - Offert-objektet
 * @param {Function} onValidityUpdate - Callback när giltighetstid uppdateras
 */
const ValidityEditor = ({ offert, onValidityUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [validityDays, setValidityDays] = useState(offert.validityDays || 60);
  const [saving, setSaving] = useState(false);

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('sv-SE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Säkerställ att vi har ett giltigt nummer
      const finalValidityDays = validityDays === '' || isNaN(validityDays) ? 60 : Math.max(1, Math.min(365, validityDays));
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/offert/${offert._id}/validity`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ validityDays: finalValidityDays })
      });

      if (response.ok) {
        onValidityUpdate(finalValidityDays);
        setValidityDays(finalValidityDays); // Uppdatera state med det korrigerade värdet
        setIsEditing(false);
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Fel vid uppdatering av giltighetstid');
      }
    } catch (error) {
      console.error('Fel vid uppdatering av giltighetstid:', error);
      alert('Fel vid uppdatering av giltighetstid');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setValidityDays(offert.validityDays || 60);
    setIsEditing(false);
  };

  if (offert.expiresAt) {
    // Om offerten har en giltighetstid (skickad)
    const now = new Date();
    const expiryDate = new Date(offert.expiresAt);
    const isExpired = now > expiryDate;
    return (
      <p className="text-xs text-white/80 mt-1">
        {isExpired ? 'Gick ut' : 'Giltig till'}: {formatDate(offert.expiresAt)}
      </p>
    );
  }

  // Om offerten är draft
  const today = new Date();
  const futureDate = new Date(today.getTime() + (validityDays * 24 * 60 * 60 * 1000));

  if (isEditing) {
    return (
      <div className="flex items-center space-x-2 mt-1">
        <input
          type="number"
          min="1"
          max="365"
          value={validityDays}
          onChange={(e) => {
            const value = e.target.value;
            if (value === '') {
              setValidityDays(''); // Tillåt tomt värde temporärt
            } else {
              const numValue = parseInt(value);
              setValidityDays(isNaN(numValue) ? 60 : numValue);
            }
          }}
          className="w-16 px-2 py-1 text-xs text-gray-900 bg-white rounded border focus:outline-none focus:ring-2 focus:ring-white/50"
        />
        <span className="text-xs text-white/80">dagar</span>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-2 py-1 text-xs bg-white/20 hover:bg-white/30 rounded transition-colors disabled:opacity-50"
        >
          {saving ? '...' : '✓'}
        </button>
        <button
          onClick={handleCancel}
          className="px-2 py-1 text-xs bg-white/10 hover:bg-white/20 rounded transition-colors"
        >
          ✗
        </button>
      </div>
    );
  }

  return (
    <p className="text-xs text-white/80 mt-1">
      <button
        onClick={() => setIsEditing(true)}
        className="hover:text-white transition-colors underline decoration-dotted"
        title="Klicka för att ändra giltighetstid"
      >
        Kommer gälla till: {formatDate(futureDate)} ({validityDays} dagar)
      </button>
    </p>
  );
};

export default ValidityEditor;

